/** Saved NDG pupils only. Read on explicit request; selection never sends mail. */
import { useEffect, useId, useMemo, useState } from "react";
import axios from "axios";
const ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const client = axios.create({ withCredentials: true, timeout: 15000,
  headers: { "Cache-Control": "no-store" } });
const text = (value) => typeof value === "string" ? value.trim() : "";
const fold = (value) => text(value).normalize("NFKC").toLocaleLowerCase("en-GB");
export const completeMailAddress = (value) => typeof value === "string"
  && value.length <= 254 && /^[^\s<>(),;:]+@[^\s<>(),;:]+\.[^\s<>(),;:]+$/.test(value)
  && !/[\x00-\x1f\x7f]/.test(value);

export function savedRecipients(payload) {
  const seen = new Set();
  return (Array.isArray(payload?.pupils) ? payload.pupils : []).slice(0, 500)
    .filter((row) => row && typeof row === "object")
    .map((row) => ({
      name: text(row.full_name || row.name) || [text(row.first_name), text(row.last_name)].filter(Boolean).join(" "),
      email: completeMailAddress(text(row.email)) ? text(row.email) : "",
    })).filter((row) => {
      const key = `${row.name}\n${row.email}`;
      if (!row.name || seen.has(key)) return false;
      seen.add(key); return true;
    }).sort((a, b) => a.name.localeCompare(b.name, "en-GB"));
}


export default function MailRecipient({ value, onChange, disabled }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("idle");
  const [query, setQuery] = useState("");
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const controller = new AbortController();
    setStatus("loading"); setRows([]);
    client.get(`${ORIGIN}/api/pupils`, { signal: controller.signal }).then(({ data }) => {
      if (!Array.isArray(data?.pupils)) throw new Error("Unexpected recipient response");
      if (active) { setRows(savedRecipients(data)); setStatus("ready"); }
    }).catch(() => { if (active) setStatus("error"); });
    return () => { active = false; controller.abort(); };
  }, [open, reload]);
  const matches = useMemo(() => rows.filter((row) =>
    fold(row.name).includes(fold(query)) || fold(row.email).includes(fold(query))), [rows, query]);
  return <div data-testid="mail-recipient-picker">
    <label htmlFor={id}>Recipient</label>
    <input id={id} aria-label="Recipient" type="email" required maxLength={254}
      autoComplete="off" autoCapitalize="none" spellCheck={false}
      value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}
      style={{ width: "100%", background: "transparent", color: "inherit", border: "1px solid #64748b",
        borderRadius: ".35rem", padding: ".7rem", font: "inherit" }} />
    <button type="button" className="karen-panel-button" disabled={disabled}
      aria-expanded={open} aria-controls={`${id}-saved`}
      onClick={() => { setOpen(!open); setQuery(""); }}>
      {open ? "Close saved pupils" : "Choose saved pupil"}
    </button>
    {open && <div id={`${id}-saved`}>
      <p>Choose a saved NDG pupil and check their full address. Phone and iCloud contacts are not connected here.</p>
      <label htmlFor={`${id}-search`}>Find saved pupil</label>
      <input id={`${id}-search`} type="search" value={query} disabled={disabled}
        autoComplete="off" onChange={(event) => setQuery(event.target.value)} />
      {status === "loading" && <p role="status">Loading saved NDG pupils…</p>}
      {status === "error" && <div role="status">
        <p>Saved pupils are unavailable. You can still enter a full email address.</p>
        <button type="button" className="karen-panel-button" disabled={disabled}
          onClick={() => setReload((n) => n + 1)}>Retry saved pupils</button>
      </div>}
      {status === "ready" && (matches.length === 0
        ? <p>No matching saved pupil. Enter their full email address above.</p>
        : <><ul style={{ listStyle: "none", padding: 0 }}>
          {matches.slice(0, 8).map((row) => <li key={`${row.name}:${row.email}`}>
            <button type="button" className="karen-panel-button" disabled={disabled || !row.email}
              style={{ display: "block", width: "100%", textAlign: "left", overflowWrap: "anywhere" }}
              onClick={() => { onChange(row.email); setOpen(false); setRows([]); setQuery(""); }}
              aria-label={row.email ? `Use ${row.name}: ${row.email}` : `${row.name}: no saved email address`}>
              <strong>{row.name}</strong><br />{row.email || "No saved email address — enter it above"}
            </button>
          </li>)}
        </ul>{matches.length > 8 && <p>Type more of the name to narrow these results.</p>}</>)}
    </div>}
  </div>;
}
